import React, { useEffect, useState } from "react";
import axios from "../axios";
import { canUseAnalysis } from "../utils/analysisAccess";

/**
 * マトリクスの「所定どおり」の印(261006追加)。
 *
 * 印の意味は「**その日の朝の所定**の車が、自分の所定の乗番の便を走った」。
 * いまの所定で過去の日を塗ってはいない（バックエンドの RegularSnapshots）。
 *
 * 所定は乗番の中身なので、運用解析と同じ範囲（ANALYSIS_ACCESS）にだけ出す。
 * マトリクス本体は誰でも見られるので、印は別の API で取って重ねる。
 * マイページで消した人には API も呼ばない。
 */

const MARK = "◆";
const COLOR = "#0ea5e9";

// 時刻の左肩に小さく上付きで付ける。**印の無い行にも同じ幅を空ける**
// （印のあるなしで時刻の位置がずれないように）。ただし表に印が 1 つも
// 無いときは空けない（呼ぶ側が align を渡さない）
const MARK_STYLE = {
  display: "inline-block",
  width: "8px",
  fontSize: "7px",
  lineHeight: 0,
  color: COLOR,
  verticalAlign: "5px",
  textAlign: "left",
};

export function wantsRegularMarks(currentUser) {
  return canUseAnalysis(currentUser) && currentUser?.show_regular_marks !== false;
}

/**
 * 印を付ける投稿の id。
 *
 * @param query    `{ depot }` か `{ favorites: true }`
 * @param reloadKey 本体を取り直したら印も取り直す（投稿・編集のあと）
 * @returns Set<number>
 */
export function useRegularMarks(currentUser, query, reloadKey) {
  const enabled = wantsRegularMarks(currentUser);
  const depot = query?.depot;
  const favorites = Boolean(query?.favorites);
  const [ids, setIds] = useState(() => new Set());

  useEffect(() => {
    if (!enabled || (!depot && !favorites)) {
      setIds(new Set());
      return;
    }

    let cancelled = false;
    const params = favorites ? "favorites=1" : `depot=${encodeURIComponent(depot)}`;

    axios
      .get(`/analysis/regular-marks?${params}`)
      .then((res) => {
        if (!cancelled) setIds(new Set(res.data.post_ids || []));
      })
      // 印が取れなくてもマトリクスは見せる
      .catch(() => {
        if (!cancelled) setIds(new Set());
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, depot, favorites, reloadKey]);

  return ids;
}

/**
 * 便の左肩に付ける印。
 *
 * `on` でなくても `align` なら同じ幅を空ける。印を出す人の表では全行に
 * `align` を渡し、セルをまたいでも時刻の位置を揃える。
 */
export function RegularMark({ on, align }) {
  if (!on && !align) return null;

  return (
    <span
      aria-label={on ? "所定どおり" : undefined}
      aria-hidden={on ? undefined : true}
      title={on ? "所定運用どおりの便（その日の朝の所定）" : undefined}
      style={{ ...MARK_STYLE, visibility: on ? "visible" : "hidden" }}
    >
      {MARK}
    </span>
  );
}

// 凡例は表に出さない。意味の説明は将来の解説ページに置く（2026-10-06 の判断）。
// 印の title（マウスを乗せたときの説明）だけは残してある
